import {
  CircleContactUser,
  MemberLeaderDetailsResponse,
  MemberRegistrationDetails,
} from 'src/api/circles';
import {
  ContactListMember,
  ContactShareConsent,
  Team,
} from 'src/pages/UserCircle/Information/types';

import { CustomerCardCustomAnswer, CustomerCardPerson } from './types';

export const parseOptionalBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value == null || value === '') {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', 'yes', 'ja', '1'].includes(normalized)) {
    return true;
  }
  if (['false', 'no', 'nei', '0'].includes(normalized)) {
    return false;
  }

  return undefined;
};

export const parseOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const pickCustomerCardAdminFields = (
  ...sources: Array<object | undefined>
): Partial<
  Pick<
    ContactListMember,
    'ehfInvoice' | 'hotelNeeded' | 'allergies' | 'comment'
  >
> => {
  const read = (keys: string[]) => {
    for (const source of sources) {
      if (!source) {
        continue;
      }
      const record = source as Record<string, unknown>;
      for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) {
          return record[key];
        }
      }
    }
    return undefined;
  };

  const ehfInvoice = parseOptionalBoolean(
    read(['ehfInvoice', 'ehf_invoice', 'ehf'])
  );
  const hotelNeeded = parseOptionalBoolean(
    read(['hotelNeeded', 'hotel_needed', 'needsHotel'])
  );
  const allergies = parseOptionalString(read(['allergies', 'allergier']));
  const comment = parseOptionalString(read(['comment', 'kommentar']));

  return {
    ...(ehfInvoice !== undefined ? { ehfInvoice } : {}),
    ...(hotelNeeded !== undefined ? { hotelNeeded } : {}),
    ...(allergies ? { allergies } : {}),
    ...(comment ? { comment } : {}),
  };
};

export const findTeamNameForMember = (
  teams: Team[],
  memberId: string
): string | undefined =>
  teams.find((team) => team.members.some((member) => member.id === memberId))
    ?.name;

export const toCustomerCardPerson = (
  member: Pick<
    ContactListMember,
    | 'id'
    | 'name'
    | 'company'
    | 'avatarUrl'
    | 'bio'
    | 'email'
    | 'phone'
    | 'notes'
    | 'contactShareConsent'
    | 'ehfInvoice'
    | 'hotelNeeded'
    | 'allergies'
    | 'comment'
  >,
  options?: { teamName?: string }
): CustomerCardPerson => ({
  id: member.id,
  name: member.name,
  company: member.company,
  avatarUrl: member.avatarUrl,
  bio: member.bio,
  email: member.email,
  phone: member.phone,
  teamName: options?.teamName,
  contactShareConsent: member.contactShareConsent,
  ...pickCustomerCardAdminFields(member),
  ...(member.comment || member.notes
    ? { comment: member.comment || member.notes }
    : {}),
});

export const buildCustomerCardPreview = (
  member: {
    id: string;
    name: string;
    company?: string;
    avatarUrl?: string;
    bio?: string;
    email?: string;
    phone?: string;
    notes?: string;
    contactShareConsent?: ContactShareConsent;
    isHidden?: boolean;
  },
  contacts: ContactListMember[],
  options?: { teamName?: string; teams?: Team[] }
): CustomerCardPerson => {
  const fromDirectory = contacts.find((item) => item.id === member.id);
  const fromMember =
    'email' in member ||
    'phone' in member ||
    'notes' in member ||
    'bio' in member ||
    'contactShareConsent' in member
      ? member
      : undefined;

  return toCustomerCardPerson(
    {
      id: member.id,
      name: member.name,
      company: member.company ?? fromDirectory?.company,
      avatarUrl: member.avatarUrl ?? fromDirectory?.avatarUrl,
      bio: fromMember?.bio ?? fromDirectory?.bio,
      email: fromMember?.email ?? fromDirectory?.email,
      phone: fromMember?.phone ?? fromDirectory?.phone,
      notes: fromMember?.notes ?? fromDirectory?.notes,
      contactShareConsent:
        fromMember?.contactShareConsent ??
        fromDirectory?.contactShareConsent ??
        (member.isHidden ? 'declined' : 'approved'),
    },
    {
      teamName:
        options?.teamName ??
        (options?.teams
          ? findTeamNameForMember(options.teams, member.id)
          : undefined),
    }
  );
};

export const hasApprovedContactShare = (
  consent?: ContactShareConsent
): boolean => consent !== 'declined';

export type YesNoTone = 'positive' | 'negative' | 'muted';

export const formatYesNo = (
  value?: boolean
): { text: string; tone: YesNoTone } => {
  if (value === true) {
    return { text: 'Ja', tone: 'positive' };
  }
  if (value === false) {
    return { text: 'Nei', tone: 'negative' };
  }
  return { text: '—', tone: 'muted' };
};

export const formatUnknownYesNo = (value: unknown) =>
  formatYesNo(parseOptionalBoolean(value));

export const formatAllergies = (value?: string): string =>
  value?.trim() || 'Ingen registrert';

export const formatComment = (value?: string): string => value?.trim() || '—';

export const formatTeamName = (value?: string): string => value?.trim() || '—';

export type ConsentTone = YesNoTone | 'warning';

export const formatConsent = (value?: ContactShareConsent) =>
  formatYesNo(
    value === 'approved' ? true : value === 'declined' ? false : undefined
  );

export const formatContactShareStatus = (
  value?: ContactShareConsent
): { text: string; tone: ConsentTone } => {
  if (value === 'approved') {
    return { text: 'Godkjent', tone: 'positive' };
  }
  if (value === 'declined') {
    return { text: 'Samtykke avslått', tone: 'warning' };
  }
  return { text: '—', tone: 'muted' };
};

export const formatContactShareShort = (
  value?: ContactShareConsent
): { text: string; tone: ConsentTone } => {
  if (value === 'approved') {
    return { text: 'Ja', tone: 'positive' };
  }
  if (value === 'declined') {
    return { text: 'Avslått', tone: 'warning' };
  }
  return { text: '—', tone: 'muted' };
};

export const formatHeaderMeta = (company?: string, jobTitle?: string): string =>
  [company, jobTitle].filter(Boolean).join(' • ');

const STRUCTURED_ANSWER_KEYS = new Set([
  'first_name',
  'surname',
  'full_name',
  'email',
  'phone_number',
  'firm',
  'job_title',
  'industry',
  'leadership_level',
  'gender',
  'invoice_firm_name',
  'org_nr',
  'invoice_email',
  'invoice_reference',
  'wants_ehf_invoice',
  'allergies',
  'allergier',
  'additional_comments',
  'comments',
  'comment',
  'kommentar',
  'wants_hotel_room',
  'hotel',
  'hotel_needed',
  'consent_1',
  'consent_2',
  'consent_3',
  'accept_terms_1',
  'accept_terms_2',
  'fill_consent_later',
]);

const KNOWN_ANSWER_LABELS: Record<string, string> = {
  first_name: 'Fornavn',
  surname: 'Etternavn',
  full_name: 'Navn',
  email: 'E-post',
  phone_number: 'Mobil',
  firm: 'Firma',
  job_title: 'Stilling',
  industry: 'Bransje',
  leadership_level: 'Ledernivå',
  gender: 'Kjønn',
  invoice_firm_name: 'Firmanavn',
  org_nr: 'Org.nr',
  invoice_email: 'Faktura e-post',
  invoice_reference: 'Referanse',
  wants_ehf_invoice: 'EHF',
  allergies: 'Allergier',
  allergier: 'Allergier',
  additional_comments: 'Kommentar',
  comments: 'Kommentar',
  comment: 'Kommentar',
  kommentar: 'Kommentar',
  wants_hotel_room: 'Ønsker hotellrom',
  hotel: 'Ønsker hotellrom',
  hotel_needed: 'Ønsker hotellrom',
  consent_1: 'Kontaktdeling',
  consent_2: 'Bilder / foto',
  consent_3: 'Markedsføring',
};

export const fieldLabelsFromSchema = (schema?: {
  schemaJson?: {
    sections?: Array<{ fields?: Array<{ id?: string; label?: string }> }>;
  };
}): Record<string, string> | undefined => {
  const fields =
    schema?.schemaJson?.sections?.flatMap((section) => section.fields ?? []) ??
    [];
  const labels = Object.fromEntries(
    fields.flatMap((field) => {
      const id = parseOptionalString(field.id);
      const label = parseOptionalString(field.label);
      return id && label ? [[id, label]] : [];
    })
  );

  return Object.keys(labels).length > 0 ? labels : undefined;
};

export const formatAnswerLabel = (
  id: string,
  labels?: Record<string, string>
): string => {
  const fromSchema = labels?.[id]?.trim();
  if (fromSchema) {
    return fromSchema;
  }
  if (KNOWN_ANSWER_LABELS[id]) {
    return KNOWN_ANSWER_LABELS[id];
  }
  const trimmed = id.replace(/_/g, ' ').trim();
  if (!trimmed) {
    return id;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

export const formatAnswerValue = (value: unknown): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nei';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    return text || undefined;
  }
  return undefined;
};

export const customAnswersFromRegistration = (
  answers: Record<string, unknown> = {},
  labels?: Record<string, string>
): CustomerCardCustomAnswer[] =>
  Object.entries(answers).flatMap(([id, value]) => {
    if (STRUCTURED_ANSWER_KEYS.has(id)) {
      return [];
    }
    const display = formatAnswerValue(value);
    if (!display) {
      return [];
    }
    return [{ id, label: formatAnswerLabel(id, labels), value: display }];
  });

const mapContactUser = (
  user: CircleContactUser,
  teamName?: string
): CustomerCardPerson => ({
  id: String(user._id),
  name: user.full_name,
  company: parseOptionalString(user.firm ?? undefined),
  avatarUrl: parseOptionalString(user.avatar_url) || user.avatar?.url,
  bio: parseOptionalString(user.bio),
  email: user.email,
  phone: user.phone_number,
  teamName,
  contactShareConsent: user.contactShareConsent,
});

const mapRegistration = (
  registration: MemberRegistrationDetails,
  fieldLabels?: Record<string, string>
): Partial<CustomerCardPerson> => {
  const ehfInvoice = parseOptionalBoolean(registration.invoice?.wants_ehf);
  const hotelNeeded = parseOptionalBoolean(registration.wants_hotel_room);
  const allergies = parseOptionalString(registration.allergies ?? undefined);
  const comment = parseOptionalString(
    registration.additional_comments ?? undefined
  );
  const firmName = parseOptionalString(
    registration.invoice?.firm_name ?? undefined
  );
  const orgNr = parseOptionalString(registration.invoice?.org_nr ?? undefined);
  const invoiceEmail = parseOptionalString(
    registration.invoice?.email ?? undefined
  );
  const reference = parseOptionalString(
    registration.invoice?.reference ?? undefined
  );

  return {
    firstName: parseOptionalString(registration.first_name ?? undefined),
    surname: parseOptionalString(registration.surname ?? undefined),
    jobTitle: parseOptionalString(registration.job_title ?? undefined),
    industry: parseOptionalString(registration.industry ?? undefined),
    leadershipLevel: parseOptionalString(
      registration.leadership_level ?? undefined
    ),
    gender: parseOptionalString(registration.gender ?? undefined),
    invoice: {
      ...(firmName ? { firmName } : {}),
      ...(orgNr ? { orgNr } : {}),
      ...(invoiceEmail ? { email: invoiceEmail } : {}),
      ...(reference ? { reference } : {}),
      ...(ehfInvoice !== undefined ? { wantsEhf: ehfInvoice } : {}),
    },
    ...(ehfInvoice !== undefined ? { ehfInvoice } : {}),
    ...(hotelNeeded !== undefined ? { hotelNeeded } : {}),
    ...(allergies ? { allergies } : {}),
    ...(comment ? { comment } : {}),
    consents: {
      contactShare: registration.consents?.contactShare,
      photos: parseOptionalBoolean(registration.consents?.photos),
      marketing: parseOptionalBoolean(registration.consents?.marketing),
    },
    customAnswers: customAnswersFromRegistration(
      registration.answers,
      fieldLabels
    ),
    hasRegistration: true,
  };
};

export const mapMemberContactToPerson = (
  user: CircleContactUser,
  options?: { teamName?: string }
): CustomerCardPerson => mapContactUser(user, options?.teamName);

export const mapLeaderMemberDetailsToPerson = (
  result: MemberLeaderDetailsResponse,
  options?: { teamName?: string; fieldLabels?: Record<string, string> }
): CustomerCardPerson => {
  const person = mapContactUser(result.user, options?.teamName);
  if (!result.registration) {
    return { ...person, hasRegistration: false };
  }

  return {
    ...person,
    ...mapRegistration(result.registration, options?.fieldLabels),
    contactShareConsent:
      result.user.contactShareConsent ??
      result.registration.consents?.contactShare,
  };
};

export const customerCardLoadErrorMessage = (status?: number): string => {
  if (status === 404) {
    return 'Personen er ikke i denne sirkelen.';
  }
  if (status === 403) {
    return 'Du har ikke tilgang til disse opplysningene.';
  }
  return 'Kunne ikke laste kundekortet.';
};
